import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  GraduationCap, Plus, Search, Trash2, Video, Image as ImageIcon, ExternalLink,
  Play, Pencil, Upload, Save, X, ChevronUp, ChevronDown, Eye, EyeOff, Link as LinkIcon,
  Clock, FileVideo, Sparkles, PackageCheck, Loader2,
} from "lucide-react";

const TIER_LABEL = { basic: "Basic", pro: "Pro", premium: "Premium" };
const TIER_COLOR = { basic: "#64748B", pro: "#0EA5E9", premium: "#F59E0B" };
const TIER_OPTIONS = [
  { v: "basic", l: "Basic — visibile a tutti" },
  { v: "pro", l: "Pro — solo tenant Pro/Premium" },
  { v: "premium", l: "Premium — solo tenant Premium" },
];

const emptyCourse = () => ({
  title: "",
  subtitle: "",
  description: "",
  cover_url: "",
  category: "Generale",
  instructor: "Salon Up",
  required_tier: "basic",
  free_preview_count: 0,
  price_eur: null,
  published: false,
  order: 0,
});

const emptyLesson = () => ({
  title: "",
  description: "",
  duration_min: 0,
  video_url: "",
  resources: [],
});

export default function AgencyFormazione() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null); // course being edited (null | course)
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/formazione/admin/courses");
      setCourses(r.data);
    } catch (e) {
      toast.error("Errore caricamento corsi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter((c) =>
      (c.title || "").toLowerCase().includes(q) ||
      (c.category || "").toLowerCase().includes(q) ||
      (c.instructor || "").toLowerCase().includes(q)
    );
  }, [courses, query]);

  const totals = useMemo(() => ({
    total: courses.length,
    published: courses.filter((c) => c.published).length,
    lessons: courses.reduce((a, c) => a + (c.lessons_count || 0), 0),
    minutes: courses.reduce((a, c) => a + (c.duration_min || 0), 0),
  }), [courses]);

  const onDelete = async (cid) => {
    if (!window.confirm("Eliminare definitivamente questo corso?")) return;
    await api.delete(`/formazione/admin/courses/${cid}`);
    toast.success("Corso eliminato");
    if (selectedId === cid) setSelectedId(null);
    load();
  };

  const onTogglePublished = async (c) => {
    await api.patch(`/formazione/admin/courses/${c.id}`, { published: !c.published });
    toast.success(c.published ? "Bozza" : "Pubblicato");
    load();
  };

  const onReorder = async (cid, dir) => {
    const idx = courses.findIndex((c) => c.id === cid);
    const swap = idx + dir;
    if (swap < 0 || swap >= courses.length) return;
    const next = courses.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setCourses(next);
    await api.post("/formazione/admin/courses/reorder", { ids: next.map((c) => c.id) });
  };

  return (
    <div data-testid="agency-formazione" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight flex items-center gap-3">
            <GraduationCap className="h-9 w-9 text-[color:var(--sp-brand)]" /> Formazione
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">
            Crea corsi, carica video e gestisci lo sblocco per tier. I titolari vedranno solo i corsi <strong>pubblicati</strong>.
          </p>
        </div>
        <button data-testid="new-course-btn" onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--sp-brand)] text-white font-bold hover:opacity-90 transition">
          <Plus className="h-4 w-4" /> Nuovo corso
        </button>
      </header>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Corsi totali" value={totals.total} icon={GraduationCap} />
        <Stat label="Pubblicati" value={totals.published} icon={PackageCheck} color="var(--sp-success)" />
        <Stat label="Lezioni" value={totals.lessons} icon={Video} />
        <Stat label="Durata totale" value={`${Math.round(totals.minutes / 60)}h`} icon={Clock} />
      </section>

      {/* Toolbar */}
      <div className="sp-card p-3 flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input placeholder="Cerca titolo, categoria, docente…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="courses-search"
            className="sp-input !pl-10 !py-1.5 text-sm w-full" />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[1,2,3,4,5,6].map((i) => (
            <div key={i} className="sp-card p-4 animate-pulse space-y-3">
              <div className="h-32 bg-[color:var(--sp-subtle)] rounded" />
              <div className="h-4 bg-[color:var(--sp-subtle)] rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="sp-card p-10 text-center text-[color:var(--sp-text-secondary)]">
          <Sparkles className="h-8 w-8 mx-auto mb-3 opacity-60" />
          Nessun corso ancora. Crea il primo per iniziare!
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((c, i) => (
            <AdminCourseCard key={c.id} course={c} index={i} total={filtered.length}
              onEdit={() => setEditing(c)}
              onDelete={() => onDelete(c.id)}
              onTogglePublished={() => onTogglePublished(c)}
              onMoveUp={() => onReorder(c.id, -1)}
              onMoveDown={() => onReorder(c.id, 1)}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCourseModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setShowCreate(false); load(); setEditing(c); }}
        />
      )}
      {editing && (
        <CourseEditorDrawer
          courseId={editing.id}
          onClose={() => setEditing(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------
function Stat({ icon: Icon, label, value, color = "var(--sp-brand)" }) {
  return (
    <div className="sp-card p-4 sp-anim">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="font-display text-2xl font-black mt-2">{value}</div>
    </div>
  );
}

function AdminCourseCard({ course, index, total, onEdit, onDelete, onTogglePublished, onMoveUp, onMoveDown }) {
  return (
    <div className="sp-card overflow-hidden group" data-testid={`admin-course-${course.id}`}>
      <div className="relative h-40 bg-gradient-to-br from-cyan-400 to-violet-500">
        {course.cover_url ? (
          <img src={course.cover_url} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GraduationCap className="h-16 w-16 text-white/40" />
          </div>
        )}
        {!course.published && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-500 text-white">Bozza</span>
        )}
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white"
              style={{ background: TIER_COLOR[course.required_tier] }}>
          {TIER_LABEL[course.required_tier]}
        </span>
      </div>
      <div className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)]">{course.category}</div>
        <h3 className="font-display font-black text-lg mt-1 line-clamp-2">{course.title || "(Senza titolo)"}</h3>
        <div className="text-xs text-[color:var(--sp-text-secondary)] mt-1 flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Video className="h-3.5 w-3.5" />{course.lessons_count} lezioni</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{Math.round((course.duration_min || 0) / 60)}h</span>
          {course.price_eur ? <span className="ml-auto font-bold">€ {course.price_eur}</span> : null}
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button data-testid={`course-edit-${course.id}`} onClick={onEdit}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[color:var(--sp-brand)] text-white text-xs font-bold hover:opacity-90">
            <Pencil className="h-3.5 w-3.5" /> Modifica
          </button>
          <button data-testid={`course-publish-${course.id}`} onClick={onTogglePublished}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border ${course.published ? "border-emerald-500 text-emerald-600 hover:bg-emerald-50" : "border-amber-500 text-amber-600 hover:bg-amber-50"}`}>
            {course.published ? <><Eye className="h-3.5 w-3.5" /> Pubblicato</> : <><EyeOff className="h-3.5 w-3.5" /> Pubblica</>}
          </button>
          <div className="ml-auto flex items-center gap-1">
            <button onClick={onMoveUp} disabled={index === 0}
              className="h-7 w-7 flex items-center justify-center rounded-full border border-[color:var(--sp-border)] disabled:opacity-30 hover:bg-[color:var(--sp-subtle)]" title="Sposta su">
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button onClick={onMoveDown} disabled={index === total - 1}
              className="h-7 w-7 flex items-center justify-center rounded-full border border-[color:var(--sp-border)] disabled:opacity-30 hover:bg-[color:var(--sp-subtle)]" title="Sposta giù">
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <button onClick={onDelete}
              data-testid={`course-delete-${course.id}`}
              className="h-7 w-7 flex items-center justify-center rounded-full border border-[color:var(--sp-danger)]/40 text-[color:var(--sp-danger)] hover:bg-[color:var(--sp-danger)]/10" title="Elimina">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateCourseModal({ onClose, onCreated }) {
  const [form, setForm] = useState(emptyCourse());
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setBusy(true);
    try {
      const r = await api.post("/formazione/admin/courses", form);
      toast.success("Corso creato");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        data-testid="new-course-modal"
        className="sp-card p-6 w-full max-w-lg space-y-4">
        <h2 className="font-display text-2xl font-black">Nuovo corso</h2>
        <Field label="Titolo *">
          <input className="sp-input" value={form.title} data-testid="new-course-title"
            onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus />
        </Field>
        <Field label="Sottotitolo">
          <input className="sp-input" value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Categoria">
            <input className="sp-input" value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </Field>
          <Field label="Docente">
            <input className="sp-input" value={form.instructor}
              onChange={(e) => setForm({ ...form, instructor: e.target.value })} />
          </Field>
        </div>
        <Field label="Tier richiesto">
          <select className="sp-input" value={form.required_tier} data-testid="new-course-tier"
            onChange={(e) => setForm({ ...form, required_tier: e.target.value })}>
            {TIER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="new-course-submit"
            className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Crea corso
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-[color:var(--sp-text-secondary)] block mb-1">{label}</span>
      {children}
    </label>
  );
}

// -----------------------------------------------------------------------------
// Editor drawer — dettaglio corso + gestione lezioni
// -----------------------------------------------------------------------------
function CourseEditorDrawer({ courseId, onClose, onChanged }) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info"); // info | lessons

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/formazione/admin/courses/${courseId}`);
      setCourse(r.data);
    } catch (e) { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [courseId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="course-editor-drawer"
        className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right">
        <header className="sticky top-0 bg-white border-b border-[color:var(--sp-border)] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-display text-2xl font-black">{loading ? "…" : (course?.title || "Corso")}</h2>
            <p className="text-xs text-[color:var(--sp-text-muted)]">ID: {courseId.slice(0, 8)}…</p>
          </div>
          <button onClick={onClose} data-testid="editor-close"
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-[color:var(--sp-subtle)]">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="border-b border-[color:var(--sp-border)] flex px-6">
          {[
            { k: "info", l: "Dettagli corso" },
            { k: "lessons", l: `Lezioni (${course?.lessons?.length || 0})` },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              className={`px-4 py-3 text-sm font-bold border-b-2 transition ${tab === t.k ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-transparent text-[color:var(--sp-text-secondary)]"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading || !course ? (
            <div className="text-center text-[color:var(--sp-text-muted)] py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : tab === "info" ? (
            <InfoTab course={course} onSaved={() => { load(); onChanged?.(); }} />
          ) : (
            <LessonsTab course={course} onChanged={() => { load(); onChanged?.(); }} />
          )}
        </div>
      </div>
    </div>
  );
}

function InfoTab({ course, onSaved }) {
  const [form, setForm] = useState({
    title: course.title || "",
    subtitle: course.subtitle || "",
    description: course.description || "",
    cover_url: course.cover_url || "",
    category: course.category || "Generale",
    instructor: course.instructor || "Salon Up",
    required_tier: course.required_tier || "basic",
    free_preview_count: course.free_preview_count || 0,
    price_eur: course.price_eur ?? "",
    published: !!course.published,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, price_eur: form.price_eur === "" ? null : Number(form.price_eur), free_preview_count: Number(form.free_preview_count || 0) };
      await api.patch(`/formazione/admin/courses/${course.id}`, body);
      toast.success("Salvato");
      onSaved?.();
    } catch (e) { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Field label="Copertina (URL o carica immagine)">
        <div className="flex items-start gap-3">
          <input className="sp-input flex-1" placeholder="https://…"
            value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
          <UploadButton accept="image" onUploaded={(u) => setForm({ ...form, cover_url: u })} label="Carica" />
        </div>
        {form.cover_url && (
          <img src={form.cover_url} alt="cover" className="mt-2 h-32 rounded-lg object-cover w-full" />
        )}
      </Field>
      <Field label="Titolo"><input className="sp-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
      <Field label="Sottotitolo"><input className="sp-input" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></Field>
      <Field label="Descrizione">
        <textarea className="sp-input" rows={4} value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Categoria"><input className="sp-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
        <Field label="Docente"><input className="sp-input" value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Tier">
          <select className="sp-input" value={form.required_tier}
            onChange={(e) => setForm({ ...form, required_tier: e.target.value })}>
            {TIER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{TIER_LABEL[o.v]}</option>)}
          </select>
        </Field>
        <Field label="Anteprima gratuita (n° lez.)">
          <input type="number" min="0" className="sp-input" value={form.free_preview_count}
            onChange={(e) => setForm({ ...form, free_preview_count: e.target.value })} />
        </Field>
        <Field label="Prezzo €">
          <input type="number" step="0.01" min="0" className="sp-input" value={form.price_eur}
            onChange={(e) => setForm({ ...form, price_eur: e.target.value })} placeholder="opzionale" />
        </Field>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={form.published}
          onChange={(e) => setForm({ ...form, published: e.target.checked })} />
        <span className="text-sm font-bold">Pubblicato (visibile ai tenant)</span>
      </label>
      <div className="pt-2 flex justify-end">
        <button onClick={save} disabled={busy} data-testid="info-save"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white font-bold hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
        </button>
      </div>
    </div>
  );
}

function LessonsTab({ course, onChanged }) {
  const [lessons, setLessons] = useState(course.lessons || []);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { setLessons(course.lessons || []); }, [course]);

  const removeLesson = async (lid) => {
    if (!window.confirm("Eliminare la lezione?")) return;
    await api.delete(`/formazione/admin/courses/${course.id}/lessons/${lid}`);
    toast.success("Lezione eliminata");
    onChanged?.();
  };

  const move = async (idx, dir) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= lessons.length) return;
    const next = lessons.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setLessons(next);
    await api.post(`/formazione/admin/courses/${course.id}/lessons/reorder`, { ids: next.map((l) => l.id) });
    onChanged?.();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[color:var(--sp-text-secondary)]">
          {lessons.length} lezioni · {Math.round(lessons.reduce((a, l) => a + (l.duration_min || 0), 0))} min totali
        </div>
        <button data-testid="add-lesson-btn" onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90">
          <Plus className="h-4 w-4" /> Aggiungi lezione
        </button>
      </div>

      {lessons.length === 0 ? (
        <div className="sp-card p-8 text-center text-sm text-[color:var(--sp-text-muted)]">
          Nessuna lezione ancora. Aggiungi la prima per iniziare.
        </div>
      ) : (
        <div className="space-y-2">
          {lessons.map((l, i) => (
            <div key={l.id} className="sp-card p-3 flex items-center gap-3" data-testid={`lesson-row-${l.id}`}>
              <div className="h-10 w-10 rounded-full bg-[color:var(--sp-subtle)] flex items-center justify-center flex-shrink-0 text-sm font-black text-[color:var(--sp-text-secondary)]">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{l.title || "(Senza titolo)"}</div>
                <div className="text-xs text-[color:var(--sp-text-muted)] flex items-center gap-2 mt-0.5">
                  <Clock className="h-3 w-3" /> {l.duration_min || 0} min
                  {l.video_url && (
                    <a href={l.video_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[color:var(--sp-brand)] hover:underline truncate max-w-[180px]">
                      <Play className="h-3 w-3" /> Anteprima
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0}
                  className="h-7 w-7 flex items-center justify-center rounded-full border border-[color:var(--sp-border)] disabled:opacity-30 hover:bg-[color:var(--sp-subtle)]">
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === lessons.length - 1}
                  className="h-7 w-7 flex items-center justify-center rounded-full border border-[color:var(--sp-border)] disabled:opacity-30 hover:bg-[color:var(--sp-subtle)]">
                  <ChevronDown className="h-3 w-3" />
                </button>
                <button onClick={() => setEditingLesson(l)} data-testid={`edit-lesson-${l.id}`}
                  className="h-7 w-7 flex items-center justify-center rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]" title="Modifica">
                  <Pencil className="h-3 w-3" />
                </button>
                <button onClick={() => removeLesson(l.id)} data-testid={`delete-lesson-${l.id}`}
                  className="h-7 w-7 flex items-center justify-center rounded-full border border-[color:var(--sp-danger)]/40 text-[color:var(--sp-danger)] hover:bg-[color:var(--sp-danger)]/10" title="Elimina">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <LessonModal
          courseId={course.id}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); onChanged?.(); }}
        />
      )}
      {editingLesson && (
        <LessonModal
          courseId={course.id}
          lesson={editingLesson}
          onClose={() => setEditingLesson(null)}
          onSaved={() => { setEditingLesson(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function LessonModal({ courseId, lesson, onClose, onSaved }) {
  const [form, setForm] = useState(() => lesson ? {
    title: lesson.title || "",
    description: lesson.description || "",
    duration_min: lesson.duration_min || 0,
    video_url: lesson.video_url || "",
    resources: lesson.resources || [],
  } : emptyLesson());
  const [busy, setBusy] = useState(false);

  const addResource = () => setForm({ ...form, resources: [...form.resources, { label: "", url: "" }] });
  const patchRes = (idx, patch) => {
    const next = form.resources.slice();
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, resources: next });
  };
  const removeRes = (idx) => setForm({ ...form, resources: form.resources.filter((_, i) => i !== idx) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        duration_min: Number(form.duration_min || 0),
        resources: form.resources.filter((r) => r.label && r.url),
      };
      if (lesson) {
        await api.patch(`/formazione/admin/courses/${courseId}/lessons/${lesson.id}`, payload);
      } else {
        await api.post(`/formazione/admin/courses/${courseId}/lessons`, payload);
      }
      toast.success(lesson ? "Lezione aggiornata" : "Lezione aggiunta");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        data-testid="lesson-modal"
        className="sp-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-4">
        <h3 className="font-display text-xl font-black">{lesson ? "Modifica lezione" : "Nuova lezione"}</h3>
        <Field label="Titolo *">
          <input className="sp-input" value={form.title} data-testid="lesson-title"
            onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus />
        </Field>
        <Field label="Descrizione">
          <textarea className="sp-input" rows={3} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Video (YouTube/Vimeo o carica file)">
          <div className="flex items-start gap-2">
            <input className="sp-input flex-1" placeholder="https://youtube.com/… o https://vimeo.com/…"
              value={form.video_url} data-testid="lesson-video-url"
              onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
            <UploadButton accept="video" onUploaded={(u) => setForm({ ...form, video_url: u })} label="Carica video" />
          </div>
          {form.video_url && (
            <div className="text-xs text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-1 truncate">
              <LinkIcon className="h-3 w-3" /> {form.video_url}
            </div>
          )}
        </Field>
        <Field label="Durata (minuti)">
          <input type="number" min="0" className="sp-input" value={form.duration_min}
            onChange={(e) => setForm({ ...form, duration_min: e.target.value })} />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-[color:var(--sp-text-secondary)]">Risorse allegate</span>
            <button type="button" onClick={addResource}
              className="text-xs inline-flex items-center gap-1 text-[color:var(--sp-brand)] font-bold hover:underline">
              <Plus className="h-3 w-3" /> Aggiungi
            </button>
          </div>
          {form.resources.length === 0 ? (
            <div className="text-xs text-[color:var(--sp-text-muted)]">Nessuna risorsa. Aggiungi link a PDF, template, ecc.</div>
          ) : (
            <div className="space-y-2">
              {form.resources.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className="sp-input flex-1" placeholder="Etichetta (es. PDF Guida)"
                    value={r.label} onChange={(e) => patchRes(i, { label: e.target.value })} />
                  <input className="sp-input flex-[2]" placeholder="URL"
                    value={r.url} onChange={(e) => patchRes(i, { url: e.target.value })} />
                  <button type="button" onClick={() => removeRes(i)}
                    className="h-8 w-8 flex items-center justify-center rounded-full text-[color:var(--sp-danger)] hover:bg-[color:var(--sp-danger)]/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="lesson-save"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </button>
        </div>
      </form>
    </div>
  );
}

function UploadButton({ accept, onUploaded, label = "Carica" }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const acceptStr = accept === "video"
    ? "video/mp4,video/quicktime,video/webm"
    : "image/jpeg,image/png,image/gif,image/webp";

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("sp_token") || "";
      const res = await fetch(`${API}/formazione/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      onUploaded?.(data.url);
      toast.success("Upload completato");
    } catch (e) {
      toast.error(e.message || "Errore upload");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept={acceptStr} className="hidden"
        data-testid={`upload-${accept}`}
        onChange={(e) => onFile(e.target.files?.[0])} />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[color:var(--sp-subtle)] hover:bg-[color:var(--sp-border)] text-sm font-bold disabled:opacity-50 flex-shrink-0">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (accept === "video" ? <FileVideo className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />)}
        {busy ? "Upload…" : label}
      </button>
    </>
  );
}
